import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { trainings } from '../data/trainings';
import CountdownTimer from '../components/CountdownTimer';
import { EVENT_START, EARLY_BIRD_PERIOD_LABEL, LATE_PERIOD_LABEL, EVENT_DATE_LABEL } from '../data/eventConfig';

// 3-Day Training Pricing
const EARLY_BIRD_PRICE_3DAY = 32999;
const LATE_PRICE_3DAY = 37999;

// 1-Day Training Pricing
const EARLY_BIRD_PRICE_1DAY = 14999;
const LATE_PRICE_1DAY = 16999;

// 2-Day Training Pricing
const EARLY_BIRD_PRICE_2DAY = 23999;
const LATE_PRICE_2DAY = 25999;

const EARLY_BIRD_PERIOD = EARLY_BIRD_PERIOD_LABEL;
const LATE_PERIOD = LATE_PERIOD_LABEL;

const TNC_ITEMS = [
  {
    title: '1. Registration & Payment',
    points: [
      'Your seat will be confirmed only after full payment of the registration fee.',
      'All fees are non-refundable. However, in case you cannot attend, you may nominate a substitute participant from your organization with prior notice.',
      'Early-bird discounts and group discounts (if applicable) will be valid only till the announced dates.',
    ],
  },
  {
    title: '2. Cancellation & Rescheduling',
    points: [
      'The organizer reserves the right to cancel or reschedule the Event due to unavoidable circumstances (e.g., trainer unavailability, insufficient participants, natural disaster, government norms, etc.).',
      'In such cases, registered participants will be offered either a full refund, or a transfer of the registration to the next available session.',
    ],
  },
  {
    title: '3. Participation Guidelines',
    points: [
      'The training is strictly for registered participants only. Sharing of lab access, or training materials with unregistered persons is not allowed.',
      'Participants are expected to maintain professional conduct during sessions. Any disruptive behavior may result in removal without refund.',
    ],
  },
  {
    title: '4. Intellectual Property',
    points: [
      'All course materials, presentations, and lab exercises provided are the intellectual property of the organizers/trainers.',
      'Copying, distributing, or reusing the material (in any form) without written permission is strictly prohibited.',
    ],
  },
  {
    title: '5. Confidentiality',
    points: [
      'Real-world case studies, lab data, or discussions during the training may involve sensitive information. Participants must not record, share, or disclose such content publicly.',
    ],
  },
  {
    title: '6. Liability Disclaimer',
    points: [
      'The organizers and trainers will make every effort to deliver high-quality learning. However, they are not responsible for any direct or indirect damages, loss of data, or financial loss arising from the use or misuse of knowledge gained in the training.',
      'Participants are responsible for ensuring they have the required system, and software tools for hands-on labs.',
    ],
  },
  {
    title: '7. Certification',
    points: [
      'Certificates of participation will be awarded only to participants who attend the training session.',
    ],
  },
  {
    title: '8. Governing Law',
    points: [
      'These Terms & Conditions are governed by the laws of India. Any disputes will fall under the jurisdiction of the courts in Mumbai High Court.',
    ],
  },
];

const TrainingRegisterPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [hash]);

  const upcomingTrainings = trainings.filter(
    t => t.status === 'upcoming' || t.status === 'active'
  );

  const threeDay = upcomingTrainings.filter(t => t.duration === '3 Days');
  const oneDay = upcomingTrainings.filter(t => t.duration === '1 Day');
  const twoDay = upcomingTrainings.filter(t => t.duration === '2 Days');

  const [expandedItems, setExpandedItems] = useState(
    Object.fromEntries(TNC_ITEMS.map((_, i) => [i, true]))
  );

  const toggleItem = (idx) => {
    setExpandedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <div className="min-h-screen pt-20">

      {/* Hero */}
      <section className="py-10 md:py-16 relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <Link
            to="/services/quarterly-trainings"
            className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm"
          >
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Trainings
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)] mb-4">Registration</h1>
          <p className="text-[var(--text-muted)] text-lg">
            Secure your seat for Byt3con September 2026 — {EVENT_DATE_LABEL}, Online. Late registration pricing {LATE_PERIOD_LABEL}.
          </p>
        </div>
      </section>

      {/* Book My Seat CTA - Above Pricing */}
      <section className="py-8 md:py-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="hero-glow bottom-0 left-1/2 -translate-x-1/2" />
        <div className="container-main relative z-10 text-center flex flex-col items-center gap-6">
          <CountdownTimer
            targetDate={EVENT_START}
            variant="inline"
            label="EVENT STARTS IN"
            expiredLabel="Event Has Started"
          />
          <a
            href="https://konfhub.com/checkout/byt3con-online-training-september-2026"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[var(--primary)] text-black font-mono font-bold px-12 py-4 text-lg uppercase tracking-widest hover:opacity-90 transition-colors duration-200"
          >
            Reserve My Seat — Late Registration Pricing
          </a>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-10 border-b border-[var(--border)] relative overflow-hidden">
        <div className="absolute inset-0 bg-[var(--bg-primary)] opacity-80" />
        <div className="container-main relative z-10">
          <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-8">./pricing</p>

          {upcomingTrainings.length === 0 && (
            <div className="overflow-x-auto mb-6" style={{ backgroundColor: 'var(--background)' }}>
              <table className="w-full min-w-[520px] border border-[var(--border)] font-mono text-sm" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr className="bg-[var(--surface)]">
                    <th className="w-2/5 text-left p-4 text-[var(--text-main)] border-b border-r border-[var(--border)] font-bold">Track</th>
                    <th className="w-1/5 text-center p-4 text-[var(--primary)] border-b border-r border-[var(--border)] font-bold">
                      Early Bird<br />
                      <span className="text-[var(--text-muted)] text-xs font-normal">{EARLY_BIRD_PERIOD}</span>
                    </th>
                    <th className="w-1/5 text-center p-4 text-[var(--accent-cyan)] border-b border-[var(--border)] font-bold">
                      Late Registration<br />
                      <span className="text-[var(--text-muted)] text-xs font-normal">{LATE_PERIOD}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[var(--border)] hover:bg-[var(--surface-highlight)] transition-colors">
                    <td className="p-4 text-[var(--text-main)] border-r border-[var(--border)]">3-Day Intensive (6 tracks — TBA)</td>
                    <td className="p-4 text-center border-r border-[var(--border)]">
                      <span className="text-[var(--primary)] text-base font-bold">₹{EARLY_BIRD_PRICE_3DAY.toLocaleString('en-IN')}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-[var(--accent-cyan)] text-base font-bold">₹{LATE_PRICE_3DAY.toLocaleString('en-IN')}</span>
                    </td>
                  </tr>
                  <tr className="hover:bg-[var(--surface-highlight)] transition-colors">
                    <td className="p-4 text-[var(--text-main)] border-r border-[var(--border)]">1-Day Masterclass (2 tracks — TBA)</td>
                    <td className="p-4 text-center border-r border-[var(--border)]">
                      <span className="text-[var(--primary)] text-base font-bold">₹{EARLY_BIRD_PRICE_1DAY.toLocaleString('en-IN')}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-[var(--accent-cyan)] text-base font-bold">₹{LATE_PRICE_1DAY.toLocaleString('en-IN')}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="font-mono text-xs text-[var(--text-muted)] mt-3">
                Full training lineup announcing soon. Deliverables include lab access, T-shirt, meals, and a completion e-Certificate.
              </p>
            </div>
          )}

          {/* 3-Day Trainings Pricing */}
          {threeDay.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-bold text-[var(--text-main)] mb-6">3-Day Trainings</h3>
              <div className="overflow-x-auto mb-6" style={{ backgroundColor: 'var(--background)' }}>
                <table className="w-full min-w-[580px] border border-[var(--border)] font-mono text-sm" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr className="bg-[var(--surface)]">
                      <th className="w-3/10 text-left p-4 text-[var(--text-main)] border-b border-r border-[var(--border)] font-bold">Training Name</th>
                      <th className="w-1/5 text-center p-4 text-[var(--primary)] border-b border-r border-[var(--border)] font-bold">
                        Early Bird Offer<br />
                        <span className="text-[var(--text-muted)] text-xs font-normal">{EARLY_BIRD_PERIOD}</span>
                      </th>
                      <th className="w-1/5 text-center p-4 text-[var(--accent-cyan)] border-b border-r border-[var(--border)] font-bold">
                        Late Registration<br />
                        <span className="text-[var(--text-muted)] text-xs font-normal">{LATE_PERIOD}</span>
                      </th>
                      <th className="w-3/10 text-center p-4 text-[var(--accent-secondary)] border-b border-[var(--border)] font-bold">
                        Deliverables
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {threeDay.map((training, idx) => (
                      <tr
                        key={training.id}
                        className={`hover:bg-[var(--surface-highlight)] transition-colors ${
                          idx < threeDay.length - 1 ? 'border-b border-[var(--border)]' : ''
                        }`}
                      >
                        <td className="w-3/10 p-4 text-[var(--text-main)] border-r border-[var(--border)] break-words">
                          <Link
                            to={`/services/quarterly-trainings/${training.id}`}
                            className="hover:text-[var(--primary)] hover:underline transition-colors duration-200"
                          >
                            {training.title}
                          </Link>
                        </td>
                        <td className="w-1/5 p-4 text-center border-r border-[var(--border)]">
                          <span className="text-[var(--primary)] text-base font-bold">
                            ₹{EARLY_BIRD_PRICE_3DAY.toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className="w-1/5 p-4 text-center border-r border-[var(--border)]">
                          <div className="flex flex-col items-center gap-2">
                            <span className="text-[var(--accent-cyan)] text-base font-bold">
                              ₹{LATE_PRICE_3DAY.toLocaleString('en-IN')}
                            </span>
                            {training.buyUrl && (
                              <a
                                href={training.buyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block border border-[var(--accent-cyan)] text-[var(--accent-cyan)] font-mono text-xs px-3 py-1 hover:bg-[var(--accent-cyan)] hover:text-black transition-colors duration-200"
                              >
                                Buy
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="w-3/10 p-4 text-left">
                          <ul className="space-y-2 text-xs text-[var(--text-main)] font-mono">
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--accent-secondary)] flex-shrink-0 mt-0.5">*</span>
                              <span>Access to the selected 3 days online training</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--accent-secondary)] flex-shrink-0 mt-0.5">*</span>
                              <span>Byt3con T-shirts</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--accent-secondary)] flex-shrink-0 mt-0.5">*</span>
                              <span>Training completion e-Certificate</span>
                            </li>
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 2-Day Trainings Pricing */}
          {twoDay.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-bold text-[var(--text-main)] mb-6">2-Day Trainings</h3>
              <div className="overflow-x-auto mb-6" style={{ backgroundColor: 'var(--background)' }}>
                <table className="w-full min-w-[580px] border border-[var(--border)] font-mono text-sm" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr className="bg-[var(--surface)]">
                      <th className="w-3/10 text-left p-4 text-[var(--text-main)] border-b border-r border-[var(--border)] font-bold">Training Name</th>
                      <th className="w-1/5 text-center p-4 text-[var(--primary)] border-b border-r border-[var(--border)] font-bold">
                        Early Bird Offer<br />
                        <span className="text-[var(--text-muted)] text-xs font-normal">{EARLY_BIRD_PERIOD}</span>
                      </th>
                      <th className="w-1/5 text-center p-4 text-[var(--accent-cyan)] border-b border-r border-[var(--border)] font-bold">
                        Late Registration<br />
                        <span className="text-[var(--text-muted)] text-xs font-normal">{LATE_PERIOD}</span>
                      </th>
                      <th className="w-3/10 text-center p-4 text-[var(--accent-secondary)] border-b border-[var(--border)] font-bold">
                        Deliverables
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {twoDay.map((training, idx) => (
                      <tr
                        key={training.id}
                        className={`hover:bg-[var(--surface-highlight)] transition-colors ${
                          idx < twoDay.length - 1 ? 'border-b border-[var(--border)]' : ''
                        }`}
                      >
                        <td className="w-3/10 p-4 text-[var(--text-main)] border-r border-[var(--border)] break-words">
                          <Link
                            to={`/services/quarterly-trainings/${training.id}`}
                            className="hover:text-[var(--primary)] hover:underline transition-colors duration-200"
                          >
                            {training.title}
                          </Link>
                        </td>
                        <td className="w-1/5 p-4 text-center border-r border-[var(--border)]">
                          <span className="text-[var(--primary)] text-base font-bold">
                            ₹{EARLY_BIRD_PRICE_2DAY.toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className="w-1/5 p-4 text-center border-r border-[var(--border)]">
                          <div className="flex flex-col items-center gap-2">
                            <span className="text-[var(--accent-cyan)] text-base font-bold">
                              ₹{LATE_PRICE_2DAY.toLocaleString('en-IN')}
                            </span>
                            {training.buyUrl && (
                              <a
                                href={training.buyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block border border-[var(--accent-cyan)] text-[var(--accent-cyan)] font-mono text-xs px-3 py-1 hover:bg-[var(--accent-cyan)] hover:text-black transition-colors duration-200"
                              >
                                Buy
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="w-3/10 p-4 text-left">
                          <ul className="space-y-2 text-xs text-[var(--text-main)] font-mono">
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--accent-secondary)] flex-shrink-0 mt-0.5">*</span>
                              <span>Access to the selected 2 days online training</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--accent-secondary)] flex-shrink-0 mt-0.5">*</span>
                              <span>Byt3con T-shirts</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--accent-secondary)] flex-shrink-0 mt-0.5">*</span>
                              <span>Training completion e-Certificate</span>
                            </li>
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 1-Day Trainings Pricing */}
          {oneDay.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-bold text-[var(--text-main)] mb-6">1-Day Trainings</h3>
              <div className="overflow-x-auto mb-6" style={{ backgroundColor: 'var(--background)' }}>
                <table className="w-full min-w-[580px] border border-[var(--border)] font-mono text-sm" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr className="bg-[var(--surface)]">
                      <th className="w-3/10 text-left p-4 text-[var(--text-main)] border-b border-r border-[var(--border)] font-bold">Training Name</th>
                      <th className="w-1/5 text-center p-4 text-[var(--primary)] border-b border-r border-[var(--border)] font-bold">
                        Early Bird Offer<br />
                        <span className="text-[var(--text-muted)] text-xs font-normal">{EARLY_BIRD_PERIOD}</span>
                      </th>
                      <th className="w-1/5 text-center p-4 text-[var(--accent-cyan)] border-b border-r border-[var(--border)] font-bold">
                        Late Registration<br />
                        <span className="text-[var(--text-muted)] text-xs font-normal">{LATE_PERIOD}</span>
                      </th>
                      <th className="w-3/10 text-center p-4 text-[var(--accent-secondary)] border-b border-[var(--border)] font-bold">
                        Deliverables
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {oneDay.map((training, idx) => (
                      <tr
                        key={training.id}
                        className={`hover:bg-[var(--surface-highlight)] transition-colors ${
                          idx < oneDay.length - 1 ? 'border-b border-[var(--border)]' : ''
                        }`}
                      >
                        <td className="w-3/10 p-4 text-[var(--text-main)] border-r border-[var(--border)] break-words">
                          <Link
                            to={`/services/quarterly-trainings/${training.id}`}
                            className="hover:text-[var(--primary)] hover:underline transition-colors duration-200"
                          >
                            {training.title}
                          </Link>
                        </td>
                        <td className="w-1/5 p-4 text-center border-r border-[var(--border)]">
                          <span className="text-[var(--primary)] text-base font-bold">
                            ₹{EARLY_BIRD_PRICE_1DAY.toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className="w-1/5 p-4 text-center border-r border-[var(--border)]">
                          <div className="flex flex-col items-center gap-2">
                            <span className="text-[var(--accent-cyan)] text-base font-bold">
                              ₹{LATE_PRICE_1DAY.toLocaleString('en-IN')}
                            </span>
                            {training.buyUrl && (
                              <a
                                href={training.buyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block border border-[var(--accent-cyan)] text-[var(--accent-cyan)] font-mono text-xs px-3 py-1 hover:bg-[var(--accent-cyan)] hover:text-black transition-colors duration-200"
                              >
                                Buy
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="w-3/10 p-4 text-left">
                          <ul className="space-y-2 text-xs text-[var(--text-main)] font-mono">
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--accent-secondary)] flex-shrink-0 mt-0.5">*</span>
                              <span>Access to the selected 1 day online training</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--accent-secondary)] flex-shrink-0 mt-0.5">*</span>
                              <span>Training completion e-Certificate</span>
                            </li>
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-sm text-[var(--text-muted)] border-l-2 border-[var(--error)] pl-4 mb-10">
            All rates mentioned are exclusive of applicable taxes. An additional 18% GST will be added on the above value. In case of online payments, any payment gateway charges or transaction fees will also be added additionally.
          </p>

          {/* Discounts */}
          <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./group_discounts</p>
          <div className="overflow-x-auto" style={{ backgroundColor: 'var(--background)' }}>
            <table className="w-full min-w-[320px] border border-[var(--border)] font-mono text-sm">
              <thead>
                <tr className="bg-[var(--surface)]">
                  <th className="text-center p-4 text-[var(--text-main)] border-b border-r border-[var(--border)] font-bold">Discount Offer</th>
                  <th className="text-center p-4 text-[var(--text-main)] border-b border-[var(--border)] font-bold">No. of Participants</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-[var(--surface-highlight)] transition-colors">
                  <td className="p-4 text-center border-r border-[var(--border)]">
                    <span className="text-[var(--primary)] text-lg font-bold">10%</span>
                  </td>
                  <td className="p-4 text-center text-[var(--text-main)]">5 or more passes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Terms & Conditions */}
      <section className="py-10 border-b border-[var(--border)] relative overflow-hidden">
        <div className="absolute inset-0 bg-[var(--bg-primary)] opacity-80" />
        <div className="container-main relative z-10">
          <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-4">./terms_and_conditions</p>
          <p className="text-[var(--text-muted)] mb-8">
            By registering for the 'Byt3con' Information Security Training Session, you agree to the following Terms &amp; Conditions:
          </p>
          <div className="space-y-3">
            {TNC_ITEMS.map((item, idx) => (
              <div key={idx} className="border border-[var(--border)] bg-[var(--surface)]">
                <button
                  onClick={() => toggleItem(idx)}
                  className="w-full text-left p-5 flex items-center justify-between hover:bg-[var(--surface-highlight)] transition-colors duration-200"
                >
                  <span className="font-mono font-bold text-[var(--text-main)]">{item.title}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-[var(--text-muted)] flex-shrink-0 transition-transform duration-300 ${
                      expandedItems[idx] ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedItems[idx] && (
                  <div className="border-t border-[var(--border)] px-5 py-4 space-y-2">
                    {item.points.map((point, pIdx) => (
                      <div key={pIdx} className="flex items-start gap-3">
                        <span className="text-[var(--primary)] font-mono mt-1 flex-shrink-0">•</span>
                        <p className="text-[var(--text-muted)] text-sm leading-relaxed">{point}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book My Seat CTA */}
      <section className="py-8 md:py-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="hero-glow bottom-0 left-1/2 -translate-x-1/2" />
        <div className="container-main relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)] mb-4">Ready to Secure Your Spot?</h2>
          <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto mb-8">
            Late registration is now open — {LATE_PERIOD_LABEL}.
          </p>
          <a
            href="https://konfhub.com/checkout/byt3con-online-training-september-2026"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[var(--primary)] text-black font-mono font-bold px-12 py-4 text-lg uppercase tracking-widest hover:opacity-90 transition-colors duration-200"
          >
            Reserve My Seat — Late Registration Pricing
          </a>
        </div>
      </section>

    </div>
  );
};

export default TrainingRegisterPage;
